import React from 'react';

import { Box } from '@mui/material';
import CurrencyFormatter from '@apigrowfinance/pbolt-currency-formatter';
import DateFormatter from '@apigrowfinance/pbolt-date-formatter';
import { GBPCurrency } from '@apigrowfinance/pbolt-icons';
import IllustratedMessage from '@apigrowfinance/pbolt-illustrated-message';
import { Panel, PanelContent, PanelTitle } from '@apigrowfinance/pbolt-panel';
import { FormattedMessage } from 'react-intl';

import {
    LoanItemExpanded,
    RepaymentSchedule,
} from '@clients/domains/lending/lending';
import LoadingErrorMessage from '@components/LoadingErrorMessage/LoadingErrorMessage';
import DataVisualization from '@components/overviewPanels/loanRepaymentsPanel/DataVisualization';
import TextBlock from '@components/overviewPanels/loanRepaymentsPanel/TextBlock';
import { useAccount } from '@contexts/AccountProvider';
import { RepaymentStatus } from '@enums/lending/repaymentStatus';
import useRetrieveAccountLoanDetails from '@hooks/useRetrieveAccountLoanDetails';

const LoanRepaymentsPanel = () => {
    const { program } = useAccount();
    const { data, isLoading, isError } = useRetrieveAccountLoanDetails({
        expand: true,
    });
    const loanDetails = data as LoanItemExpanded;
    const hasLoan = data?.loan_id;

    const totalRepaymentsToMake = loanDetails?.repayment_schedule?.length || 0;
    const totalRepaymentsComplete =
        loanDetails?.repayment_schedule?.filter(
            (repayment: RepaymentSchedule) =>
                repayment.status === RepaymentStatus.DONE ||
                repayment.status === RepaymentStatus.PAID ||
                repayment.status === RepaymentStatus.PROCESSING,
        )?.length || 0;
    const nextRepayment =
        loanDetails?.repayment_schedule[totalRepaymentsComplete];
    const nextPaymentAmount = nextRepayment?.amount_due?.total;
    const nextDueDate = <DateFormatter date={nextRepayment?.due_date} />;
    const finalPaymentDue =
        loanDetails?.repayment_schedule[totalRepaymentsToMake - 1] != null ? (
            <DateFormatter
                date={
                    loanDetails?.repayment_schedule[totalRepaymentsToMake - 1]
                        ?.due_date
                }
            />
        ) : (
            <FormattedMessage
                defaultMessage="N/A"
                id="LoanRepaymentsPanel.notApplicable"
            />
        );
    const finishedRepayments =
        totalRepaymentsComplete === totalRepaymentsToMake;

    const showNextPayment = !finishedRepayments || isLoading;
    const showNextPaymentAmount =
        (!finishedRepayments && nextPaymentAmount != null) || isLoading;

    const panelContent = isError ? (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            margin="auto"
            data-testid="repayment-panel-error">
            <LoadingErrorMessage />
        </Box>
    ) : (
        <Box width="100%" display="flex" flexDirection="column" mt={5}>
            <Box width="100%" display="flex">
                <TextBlock
                    isLoading={isLoading}
                    title={
                        <FormattedMessage
                            id="LoanRepaymentsPanel.TotalPayments"
                            defaultMessage="Total payments"
                        />
                    }
                    data={`${totalRepaymentsComplete}/${totalRepaymentsToMake}`}
                    blockName="total-payments"
                />
                {showNextPayment && (
                    <TextBlock
                        isLoading={isLoading}
                        flexRight
                        title={
                            <FormattedMessage
                                id="LoanRepaymentsPanel.NextPaymentDue"
                                defaultMessage="Next payment due"
                            />
                        }
                        data={nextDueDate}
                        blockName="next-payment-due"
                    />
                )}
            </Box>
            <Box my={3}>
                <DataVisualization
                    isLoading={isLoading}
                    repaymentSchedule={loanDetails?.repayment_schedule}
                    currentPaymentIndex={totalRepaymentsComplete}
                />
            </Box>
            <Box width="100%" display="flex">
                {showNextPaymentAmount && (
                    <TextBlock
                        isLoading={isLoading}
                        title={
                            <FormattedMessage
                                id="LoanRepaymentsPanel.NextPaymentAmount"
                                defaultMessage="Next payment amount"
                            />
                        }
                        data={
                            <CurrencyFormatter
                                amount={nextPaymentAmount}
                                currency={program?.currency_numeric_code}
                            />
                        }
                        blockName="next-payment-amount"
                    />
                )}
                <TextBlock
                    isLoading={isLoading}
                    flexRight
                    title={
                        <FormattedMessage
                            id="LoanRepaymentsPanel.FinalPaymentDate"
                            defaultMessage="Final payment date"
                        />
                    }
                    data={finalPaymentDue}
                    blockName="final-payment-date"
                />
            </Box>
        </Box>
    );

    return (
        <Panel>
            <PanelTitle loading={isLoading} addPadding hideArrows>
                <FormattedMessage
                    id="LoanRepaymentsPanel.Repayments"
                    defaultMessage="Repayments"
                />
            </PanelTitle>
            <PanelContent>
                {!isLoading && hasLoan === undefined ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        margin="auto"
                        data-testid="repayment-panel-error">
                        <IllustratedMessage
                            icon={<GBPCurrency color="inherit" />}
                            messageMain={
                                <FormattedMessage
                                    id="LoanRepaymentsPanel.no`repayment"
                                    defaultMessage="No repayments"
                                />
                            }
                            messageSub={
                                <FormattedMessage
                                    id="LoanRepaymentsPanel.noRepaymentsFound"
                                    defaultMessage="No repayments were found for this account"
                                />
                            }
                        />
                    </Box>
                ) : (
                    panelContent
                )}
            </PanelContent>
        </Panel>
    );
};

export default LoanRepaymentsPanel;
